import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Table,
  Button,
  Row,
  Col,
  FormControl,
  InputGroup,
} from "react-bootstrap";
import { getUserSession } from "../component/userInfo";
import PagingNav from "../component/PagingNav";

const AdminBoard = () => {
  const [posts, setPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage, setPostsPerPage] = useState(10);
  const [paging, setPaging] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [tempSearchKeyword, setTempSearchKeyword] = useState("");

  useEffect(() => {
    fetchPosts(currentPage, postsPerPage, searchKeyword);
  }, [currentPage, postsPerPage, searchKeyword]);

  const fetchPosts = async (page, perpage, keyword) => {
    const userSession = getUserSession();
    const formData = new FormData();
    formData.append("uid", userSession.uid);
    formData.append("action", "list");
    formData.append("page", page);
    formData.append("perpage", perpage);
    if (keyword) {
      formData.append("searchKeyword", keyword);
    }

    try {
      const res = await fetch(
        "https://work.wekoinonia.org/json/admin_board.php",
        {
          method: "POST",
          body: formData,
        }
      );
      const data = await res.json();
      if (data.success) {
        setPosts(data.posts);
        setPaging(data.paging);
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error("Failed to fetch posts:", error);
      // Mock data for UI development
      const mockData = {
        success: true,
        posts: [
          {
            post_id: 1,
            title: "첫 번째 게시글",
            creator: "admin_user_1",
            idate: "2025-09-12 10:00:00",
          },
          {
            post_id: 2,
            title: "두 번째 게시글",
            creator: "admin_user_2",
            idate: "2025-09-12 11:30:00",
          },
        ],
        paging: {
          totalPosts: 2,
          currentPage: 1,
          postsPerPage: 10,
          totalPages: 1,
        },
      };
      setPosts(mockData.posts);
      setPaging(mockData.paging);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("정말로 이 게시글을 삭제하시겠습니까?")) {
      return;
    }

    const userSession = getUserSession();
    const formData = new FormData();
    formData.append("uid", userSession.uid);
    formData.append("action", "delete");
    formData.append("post_id", postId);

    try {
      const res = await fetch(
        "https://work.wekoinonia.org/json/admin_board.php",
        {
          method: "POST",
          body: formData,
        }
      );
      const data = await res.json();
      if (data.success) {
        fetchPosts(currentPage, postsPerPage, searchKeyword);
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error("Failed to delete post:", error);
      alert("게시글 삭제에 실패했습니다.");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = () => {
    setSearchKeyword(tempSearchKeyword);
    setCurrentPage(1);
  };

  const formatDateWithTooltip = (dateTimeString) => {
    if (!dateTimeString) return "";
    const date = new Date(dateTimeString);
    const today = new Date();

    const fullDateTime = date.toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    if (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    ) {
      // It's today, return time with full date as tooltip
      return (
        <span title={fullDateTime}>
          {date.toLocaleTimeString("ko-KR", { hour12: false })}
        </span>
      );
    } else {
      // Not today, return date with full date as tooltip
      return (
        <span title={fullDateTime}>{date.toISOString().split("T")[0]}</span>
      );
    }
  };

  return (
    <div>
      <h1 className="h3 mb-2 text-gray-800">운영자 게시판</h1>
      <p className="mb-4">운영자 전용 게시판입니다.</p>

      <div className="card shadow mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <InputGroup style={{ width: "300px" }}>
              <FormControl
                placeholder="제목 또는 내용으로 검색"
                value={tempSearchKeyword}
                onChange={(e) => setTempSearchKeyword(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
              <Button variant="outline-secondary" onClick={handleSearch}>
                검색
              </Button>
            </InputGroup>
            <Link to="/adminboard/form">
              <Button variant="primary">새 글 작성</Button>
            </Link>
          </div>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>제목</th>
                <th>작성자</th> {/* Changed from 작성자 UID */}
                <th>작성일</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {posts.length > 0 ? (
                posts.map((post) => (
                  <tr key={post.post_id}>
                    <td>{post.post_id}</td>
                    <td>
                      <Link to={`/adminboard/view/${post.post_id}`}>
                        {post.title}
                      </Link>
                    </td>
                    <td>{post.creator_name}</td> {/* Displays creator UID. For creator_name, backend needs to provide it. */}
                    <td>{formatDateWithTooltip(post.idate)}</td>
                    <td>
                      <Link to={`/adminboard/form/${post.post_id}`}>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2"
                        >
                          수정
                        </Button>
                      </Link>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(post.post_id)}
                      >
                        삭제
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    게시글이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          {paging && (
            <Row className="mt-3">
              <Col className="d-flex justify-content-center">
                <PagingNav paging={paging} callback={handlePageChange} />
              </Col>
            </Row>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBoard;
